/**
 * vue-fragment@1.6.0 patches Node.prototype.removeChild and, on EVERY removal,
 * replaces nextSibling/previousSibling on the neighbours with frozen own
 * properties. Those snapshots never update again, so code that walks the DOM by
 * sibling — Vue's updateChildren and vuedraggable's insertNodeAt — eventually
 * passes a detached node to insertBefore and throws NotFoundError.
 *
 * Undo the freeze for plain nodes. Fragment placeholders and fragment children
 * keep theirs: vue-fragment needs them to emulate its virtual sibling chain.
 *
 * vue-fragment also cannot MOVE or REMOVE a mounted <fragment>: insertBefore walks
 * head → tail by `nextSibling` after moving each node into a DocumentFragment
 * (always null → "appendChild: parameter 1 is not of type 'Node'", or a TypeError
 * for a null ref), and removeChild loops on a possibly frozen `__head.nextSibling`.
 * Vue moves a fragment whenever siblings before and after it change in one render;
 * a throw inside Vue's patch skips resetSchedulerState, so the whole page stops
 * re-rendering. Both operations are re-implemented here over the native chain.
 *
 * Must be called AFTER Vue.use(Plugin) from vue-fragment.
 */

const nativeNext = Object.getOwnPropertyDescriptor(Node.prototype, 'nextSibling').get;
const nativePrev = Object.getOwnPropertyDescriptor(Node.prototype, 'previousSibling').get;
const nativeParent = Object.getOwnPropertyDescriptor(Node.prototype, 'parentNode').get;

const hasOwn = (node, key) => Object.prototype.hasOwnProperty.call(node, key);

const isFragmentRelated = (node) =>
  !!node && (node.__isFragment === true || node.__isFragmentChild__ === true);

const isMountedFragment = (node) => !!node && node.__isFragment === true && node.__isMounted === true;

const thaw = (node) => {
  if (!node || isFragmentRelated(node)) return;

  ['nextSibling', 'previousSibling'].forEach((key) => {
    if (!hasOwn(node, key)) return;
    // A frozen pointer that spans a <fragment> is load-bearing — keep it.
    if (isFragmentRelated(node[key])) return;

    delete node[key];
  });
};

// Same shape as vue-fragment's own freeze, so its later re-freezes still apply.
const freeze = (node, key, get) => {
  Object.defineProperty(node, key, { configurable: true, get, set() {} });
};

// The placeholder's siblings are read from where its nodes actually are. vue-fragment
// freezes a snapshot on mount, which Vue then uses as the insert-after reference
// long after those nodes have moved, putting elements in the wrong order.
const linkLiveSiblings = (fragment) => {
  fragment.__head.__fragmentHeadOf = fragment;
  fragment.__tail.__fragmentTailOf = fragment;

  freeze(fragment, 'nextSibling', function () {
    const next = nativeNext.call(this.__tail);

    return (next && next.__fragmentHeadOf) || next;
  });
  freeze(fragment, 'previousSibling', function () {
    const prev = nativePrev.call(this.__head);

    return (prev && prev.__fragmentTailOf) || prev;
  });
};

// vue-fragment ends its mount with `__isMounted = true`, right after freezing the
// snapshot; swap in live siblings at that point.
const linkLiveSiblingsOnMount = (fragment) => {
  let mounted = fragment.__isMounted;

  Object.defineProperty(fragment, '__isMounted', {
    configurable: true,
    get: () => mounted,
    set(value) {
      mounted = value;

      if (value) linkLiveSiblings(fragment);
    },
  });
};

// Neighbours frozen to point at the fragment go stale once it moves or leaves.
const unlinkNeighbours = (fragment) => {
  [['previousSibling', 'nextSibling'], ['nextSibling', 'previousSibling']].forEach(([side, back]) => {
    const neighbour = fragment[side];

    if (!neighbour || !hasOwn(neighbour, back) || neighbour[back] !== fragment) return;

    if (isMountedFragment(neighbour)) {
      linkLiveSiblings(neighbour);
    } else {
      delete neighbour[back];
    }
  });
};

// head … tail in document order, read through the native getters.
const fragmentNodes = (fragment) => {
  const nodes = [];

  for (let node = fragment.__head; node; node = nativeNext.call(node)) {
    nodes.push(node);

    if (node === fragment.__tail) return nodes;
  }

  return [fragment.__head, fragment.__tail];
};

const moveMountedFragment = (parent, fragment, ref) => {
  const target = isMountedFragment(ref) ? ref.__head : ref;
  const nodes = fragmentNodes(fragment);

  if (fragment === ref || nodes.includes(target)) return fragment;

  unlinkNeighbours(fragment);

  if (target && nativeParent.call(target) === parent) {
    target.before(...nodes);
  } else {
    parent.append(...nodes);
  }

  freeze(fragment, 'parentNode', () => parent);
  linkLiveSiblings(fragment);

  return fragment;
};

const removeMountedFragment = (fragment) => {
  const nodes = fragmentNodes(fragment);
  const prev = nativePrev.call(nodes[0]);
  const next = nativeNext.call(nodes[nodes.length - 1]);

  unlinkNeighbours(fragment);

  // Park the nodes together so a later re-insert can still walk head → tail.
  document.createDocumentFragment().append(...nodes);

  Object.defineProperty(fragment, 'parentNode', { configurable: true, writable: true, value: null });
  thaw(prev);
  thaw(next);

  return fragment;
};

export default function installFragmentSiblingFix() {
  if (typeof Node === 'undefined' || Node.prototype.removeChild.__fragmentSiblingFix) return;

  const patched = Node.prototype.removeChild;

  function removeChild(child) {
    if (!this.__isFragment && isMountedFragment(child)) return removeMountedFragment(child);

    // Placeholders vue-fragment is about to re-freeze around the removed node.
    const placeholders = [child.previousSibling, child.nextSibling].filter(isMountedFragment);
    let result;

    if (this.__isFragment || isFragmentRelated(child)) {
      result = patched.call(this, child);
    } else {
      // Read through the prototype getters: own props may already be frozen/stale.
      const prev = nativePrev.call(child);
      const next = nativeNext.call(child);

      result = patched.call(this, child);

      thaw(prev);
      thaw(next);
    }

    placeholders.forEach(linkLiveSiblings);

    // vue-fragment detaches its placeholder div while mounting.
    if (child.__isFragment === true && child.__isMounted === false) linkLiveSiblingsOnMount(child);

    return result;
  }

  removeChild.__fragmentSiblingFix = true;
  Node.prototype.removeChild = removeChild;

  const patchedInsertBefore = Node.prototype.insertBefore;

  // A fragment container forwards to its real parent, which lands back here.
  function insertBefore(node, ref) {
    if (!this.__isFragment && isMountedFragment(node)) return moveMountedFragment(this, node, ref);

    return patchedInsertBefore.apply(this, arguments);
  }

  Node.prototype.insertBefore = insertBefore;
}
